import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import * as XLSX from "xlsx";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const MODOS = [
  { key: "rbd", label: "Por RBD" },
  { key: "zona", label: "Por zona" },
  { key: "oficio", label: "Por oficio" },
];

const DESCRIPCION_MODO = {
  rbd: "Consulta el detalle devengable de los RBD seleccionados y lo agrega a la tabla.",
  zona: "Consulta el detalle devengable de todos los RBD de una zona y lo agrega a la tabla.",
  oficio: "Recalcula y almacena los devengos del oficio, y deja un archivo por operador disponible para descarga.",
};

const MIN_CARACTERES_BUSQUEDA = 2;
const RETARDO_BUSQUEDA = 350;

function periodosDisponibles() {
  const base = dayjs().startOf("month");
  return Array.from({ length: 36 }, (_, i) =>
    base.subtract(i, "month").format("YYYY-MM-01")
  );
}

function etiquetaPeriodo(periodo) {
  if (!periodo) return "—";
  const d = dayjs(periodo);
  return `${MESES[d.month()]} ${d.year()}`;
}

function formatoMonto(valor) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  });
}

function padOficio(n) {
  const s = String(n ?? "");
  return s.length < 4 ? s.padStart(4, "0") : s;
}

async function describirError(e) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  let detalle = null;
  try {
    if (data instanceof Blob) {
      detalle = JSON.parse(await data.text())?.detail;
    } else if (data) {
      detalle = data.detail;
    }
  } catch {
    detalle = null;
  }

  if (detalle) return detalle;
  if (status === 403) return "No tienes privilegios para generar devengos.";
  if (status === 404) return "No se encontraron datos devengables para la selección.";
  return "No se pudo obtener el detalle. Intenta nuevamente.";
}

export default function DevengosAdministracion() {
  const { setPageTitle } = useContext(GeneralContext);

  const periodos = useMemo(() => periodosDisponibles(), []);
  const [modo, setModo] = useState("rbd");
  const [periodo, setPeriodo] = useState(periodos[1] ?? periodos[0]);

  const [rbds, setRbds] = useState([]);
  const [inputRbd, setInputRbd] = useState("");
  const [opcionesRbd, setOpcionesRbd] = useState([]);
  const [buscandoRbd, setBuscandoRbd] = useState(false);

  const [zonas, setZonas] = useState([]);
  const [idZona, setIdZona] = useState("");
  const [cargandoZonas, setCargandoZonas] = useState(true);

  const [oficios, setOficios] = useState([]);
  const [idOficio, setIdOficio] = useState("");

  // Resultado acumulado: una fila de resumen por RBD, su detalle mensual y
  // qué RBD entran al archivo.
  const [filas, setFilas] = useState([]);
  const [detallePorRbd, setDetallePorRbd] = useState({});
  const [columnas, setColumnas] = useState([]);
  const [seleccion, setSeleccion] = useState([]);

  // Incluye, para cada RBD, el mes siguiente a su último devengo dentro del
  // mismo detalle: mismas columnas, misma tabla y mismo archivo. Va marcado con
  // es_mes_siguiente para poder distinguirlo sin separarlo.
  const [incluirMesSiguiente, setIncluirMesSiguiente] = useState(false);

  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);
  const [mensajeOk, setMensajeOk] = useState(null);
  const [resultadoOficio, setResultadoOficio] = useState(null);
  const [operadores, setOperadores] = useState([]);

  useEffect(() => {
    setPageTitle?.("GENERACIÓN DE DEVENGOS");
  }, []);

  useEffect(() => {
    axios
      .getZonasDevengo()
      .then((res) => setZonas(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setZonas([]))
      .finally(() => setCargandoZonas(false));

    axios
      .getOficiosHistorial()
      .then((res) => setOficios(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setOficios([]));

    axios
      .getAllOperadores()
      .then((res) => setOperadores(res?.data ?? []))
      .catch(() => setOperadores([]));
  }, []);

  const busquedaRef = useRef(0);

  useEffect(() => {
    const termino = inputRbd.trim();
    if (termino.length < MIN_CARACTERES_BUSQUEDA) {
      setOpcionesRbd([]);
      setBuscandoRbd(false);
      return;
    }

    const miBusqueda = ++busquedaRef.current;
    setBuscandoRbd(true);

    const timer = setTimeout(() => {
      axios
        .getRBDDevengo(termino)
        .then((res) => {
          if (busquedaRef.current !== miBusqueda) return;
          setOpcionesRbd(res?.data?.rbd_list ?? []);
        })
        .catch(() => {
          if (busquedaRef.current !== miBusqueda) return;
          setOpcionesRbd([]);
        })
        .finally(() => {
          if (busquedaRef.current !== miBusqueda) return;
          setBuscandoRbd(false);
        });
    }, RETARDO_BUSQUEDA);

    return () => clearTimeout(timer);
  }, [inputRbd]);

  const nombreOperador = (id) =>
    operadores.find((o) => o.id_operador === id)?.nombre_operador ?? `Operador ${id}`;

  const oficiosOrdenados = useMemo(
    () =>
      [...oficios].sort(
        (a, b) => new Date(b.fecha_oficio ?? 0) - new Date(a.fecha_oficio ?? 0)
      ),
    [oficios]
  );

  const seleccionados = useMemo(() => new Set(seleccion), [seleccion]);

  const totales = useMemo(
    () =>
      filas
        .filter((f) => seleccionados.has(f.rbd))
        .reduce(
          (acc, f) => ({
            rbd: acc.rbd + 1,
            periodos: acc.periodos + (f.periodos_devengables ?? f.periodos ?? 0),
            facturar: acc.facturar + (f.total_valor_a_facturar ?? 0),
            descuento: acc.descuento + (f.total_descuento ?? 0),
            devengado: acc.devengado + (f.total_devengado ?? 0),
          }),
          { rbd: 0, periodos: 0, facturar: 0, descuento: 0, devengado: 0 }
        ),
    [filas, seleccionados]
  );

  const puedeConsultar =
    (modo === "rbd" && rbds.length > 0) ||
    (modo === "zona" && idZona !== "") ||
    (modo === "oficio" && idOficio !== "");

  const limpiarTabla = () => {
    setFilas([]);
    setDetallePorRbd({});
    setColumnas([]);
    setSeleccion([]);
  };

  const cambiarModo = (nuevo) => {
    if (!nuevo || nuevo === modo) return;
    setModo(nuevo);
    setError(null);
    setMensajeOk(null);
    setResultadoOficio(null);
  };

  const cambiarPeriodo = (nuevo) => {
    setPeriodo(nuevo);
    setError(null);
    if (filas.length > 0) {
      limpiarTabla();
      setMensajeOk(
        `Se limpió la tabla porque cambió el período de corte a ${etiquetaPeriodo(nuevo)}.`
      );
    }
  };

  const cambiarMesSiguiente = (valor) => {
    setIncluirMesSiguiente(valor);
    setError(null);
    if (filas.length > 0) {
      limpiarTabla();
      setMensajeOk(
        valor
          ? "Se limpió la tabla: las próximas consultas incluirán el mes siguiente al último mes devengado."
          : "Se limpió la tabla: las próximas consultas solo traerán los períodos devengables."
      );
    }
  };

  const alternarRbd = (rbd) =>
    setSeleccion((prev) =>
      prev.includes(rbd) ? prev.filter((r) => r !== rbd) : [...prev, rbd]
    );

  const alternarTodos = () =>
    setSeleccion((prev) =>
      filas.every((f) => prev.includes(f.rbd)) ? [] : filas.map((f) => f.rbd)
    );

  const quitarNoSeleccionados = () => {
    setFilas((prev) => prev.filter((f) => seleccionados.has(f.rbd)));
    setDetallePorRbd((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([rbd]) => seleccionados.has(rbd)))
    );
  };

  /**
   * Qué pasó con la opción del mes siguiente, dicho explícitamente.
   *
   * Sin esto la opción parece no hacer nada: si el corte es el último período
   * con prefactura, el mes posterior no existe todavía y el resultado es
   * idéntico al de no marcarla. Distinguir "no hay datos" de "el servidor no
   * soporta la opción" evita perseguir un bug que no está.
   */
  const detalleMesSiguiente = (data) => {
    if (!incluirMesSiguiente) return "";

    const columnas = Array.isArray(data?.columnas) ? data.columnas : [];
    if (!columnas.includes("es_mes_siguiente")) {
      return " El servidor todavía no soporta la opción del mes siguiente, así que se ignoró.";
    }

    const extra = (data?.detalle ?? []).filter((d) => d.es_mes_siguiente).length;
    if (extra === 0) {
      return (
        " Sin registros del mes siguiente: no hay prefactura en el mes posterior al" +
        " último mes devengado de estos RBD."
      );
    }
    return ` Incluye ${extra} ${extra === 1 ? "registro" : "registros"} del mes siguiente al último mes devengado.`;
  };

  const acumularResultado = (data) => {
    const nuevas = (data?.resumen ?? []).map((r) => ({ ...r, rbd: String(r.rbd) }));

    setFilas((prev) => {
      const porRbd = new Map(prev.map((f) => [f.rbd, f]));
      nuevas.forEach((f) => porRbd.set(f.rbd, f));
      return Array.from(porRbd.values()).sort((a, b) =>
        a.rbd.localeCompare(b.rbd, "es", { numeric: true })
      );
    });

    const detalleNuevo = {};
    (data?.detalle ?? []).forEach((d) => {
      const rbd = String(d.rbd);
      if (!detalleNuevo[rbd]) detalleNuevo[rbd] = [];
      detalleNuevo[rbd].push(d);
    });
    setDetallePorRbd((prev) => ({ ...prev, ...detalleNuevo }));

    if (Array.isArray(data?.columnas) && data.columnas.length > 0) {
      setColumnas(data.columnas);
    }

    setSeleccion((prev) => Array.from(new Set([...prev, ...nuevas.map((f) => f.rbd)])));

    return nuevas.length;
  };

  const consultar = async () => {
    setGenerando(true);
    setError(null);
    setMensajeOk(null);
    setResultadoOficio(null);

    try {
      if (modo === "oficio") {
        const res = await axios.getDevengosOficio({ id_oficio: idOficio });
        setResultadoOficio(res?.data ?? null);
        setMensajeOk("Devengos del oficio generados y almacenados.");
        return;
      }

      const res = await axios.getDevengosDetalle(
        modo === "zona"
          ? { id_zona: idZona, analisis_fin: periodo, incluir_mes_siguiente: incluirMesSiguiente }
          : { rbds, analisis_fin: periodo, incluir_mes_siguiente: incluirMesSiguiente }
      );

      const agregados = acumularResultado(res?.data);

      const base =
        modo === "zona"
          ? `Zona ${idZona}: ${agregados} RBD con devengos hasta ${etiquetaPeriodo(periodo)}.`
          : `${agregados} RBD con devengos hasta ${etiquetaPeriodo(periodo)}.`;

      setMensajeOk(`${base}${detalleMesSiguiente(res?.data)}`);
    } catch (e) {
      setError(await describirError(e));
    } finally {
      setGenerando(false);
    }
  };

  const descargarExcel = () => {
    const detalle = seleccion.flatMap((rbd) => detallePorRbd[rbd] ?? []);
    if (detalle.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(
      detalle,
      columnas.length > 0 ? { header: columnas } : undefined
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Devengos");

    const sufijo = seleccion.length === 1 ? seleccion[0] : `${seleccion.length}_rbd`;
    const marca = incluirMesSiguiente ? "_con_mes_siguiente" : "";
    XLSX.writeFile(wb, `devengos_${sufijo}_hasta_${periodo}${marca}.xlsx`);
  };

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          GENERACIÓN DE DEVENGOS
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Detalle devengable de los RBD con{" "}
          <strong>Estado de pago: Devengar</strong>, a partir de su fecha de alta de
          devengo.
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 1,
            background: "#fff",
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={modo}
            onChange={(_, valor) => cambiarModo(valor)}
            sx={{ mb: 2 }}
          >
            {MODOS.map((m) => (
              <ToggleButton
                key={m.key}
                value={m.key}
                sx={{ textTransform: "none", px: 2.5 }}
              >
                {m.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 2 }}>
            {DESCRIPCION_MODO[modo]}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            {modo === "rbd" && (
              <Box sx={{ flex: "1 1 480px", minWidth: 320 }}>
                <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
                  RBD:
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  value={rbds}
                  options={Array.from(new Set([...rbds, ...opcionesRbd]))}
                  inputValue={inputRbd}
                  loading={buscandoRbd}
                  filterSelectedOptions
                  filterOptions={(x) => x}
                  onInputChange={(_, valor) => setInputRbd(valor)}
                  onChange={(_, valor) => {
                    setRbds(valor);
                    setInputRbd("");
                  }}
                  noOptionsText={
                    inputRbd.trim().length < MIN_CARACTERES_BUSQUEDA
                      ? "Escribe al menos 2 dígitos del RBD"
                      : "Sin RBD devengados que coincidan"
                  }
                  renderTags={(valores, getTagProps) =>
                    valores.map((valor, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={valor}
                        label={valor}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={rbds.length ? "" : "Buscar RBD..."}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {buscandoRbd ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
                    {rbds.length} RBD por consultar
                  </Typography>
                  {rbds.length > 0 && (
                    <Button
                      size="small"
                      onClick={() => setRbds([])}
                      sx={{ textTransform: "none" }}
                    >
                      Limpiar
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {modo === "zona" && (
              <Box sx={{ flex: "0 0 320px" }}>
                <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
                  Zona:
                </Typography>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={idZona}
                  onChange={(e) => setIdZona(e.target.value)}
                  disabled={cargandoZonas || zonas.length === 0}
                  helperText={
                    cargandoZonas
                      ? "Cargando zonas..."
                      : zonas.length === 0
                      ? "No se pudo cargar el listado de zonas."
                      : ""
                  }
                >
                  {zonas.map((z) => (
                    <MenuItem key={z.id_zona} value={z.id_zona}>
                      {`Zona ${z.id_zona}`}
                      {z.nombre_operador ? ` — ${z.nombre_operador}` : ""}
                      {z.cantidad_rbd != null ? ` (${z.cantidad_rbd} RBD)` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {modo === "oficio" && (
              <Box sx={{ flex: "0 0 420px" }}>
                <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
                  Oficio:
                </Typography>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={idOficio}
                  onChange={(e) => setIdOficio(e.target.value)}
                >
                  {oficiosOrdenados.map((o) => (
                    <MenuItem key={o.id_oficio} value={o.id_oficio}>
                      {`N° ${padOficio(o.numero_oficio)} — ${String(o.tipo_oficio ?? "").replace(/_/g, " ")}`}
                      {o.cantidad_rbd != null ? ` (${o.cantidad_rbd} RBD)` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {modo !== "oficio" && (
              <Box sx={{ flex: "0 0 220px" }}>
                <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
                  Devengar hasta:
                </Typography>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={periodo}
                  onChange={(e) => cambiarPeriodo(e.target.value)}
                >
                  {periodos.map((p) => (
                    <MenuItem key={p} value={p}>
                      {etiquetaPeriodo(p)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {modo !== "oficio" && (
              <Box sx={{ alignSelf: "flex-end", pb: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={incluirMesSiguiente}
                      onChange={(e) => cambiarMesSiguiente(e.target.checked)}
                    />
                  }
                  label={
                    <Tooltip title="Agrega, por cada RBD, la fila de prefactura del mes posterior a su último mes devengado, con el estado de pago que tenga (Facturar, Baja, NYA). Si ese mes todavía no tiene prefactura, no se agrega nada.">
                      <Typography variant="body2">
                        Incluir el mes siguiente al devengo
                      </Typography>
                    </Tooltip>
                  }
                />
              </Box>
            )}

            <Box sx={{ alignSelf: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={
                  generando ? (
                    <CircularProgress size={16} />
                  ) : modo === "oficio" ? (
                    <DownloadIcon />
                  ) : (
                    <AddIcon />
                  )
                }
                disabled={!puedeConsultar || generando}
                onClick={consultar}
                sx={{
                  background: "#C8D8F0",
                  color: "#1565c0",
                  boxShadow: "none",
                  "&:hover": { background: "#b3c8e8", boxShadow: "none" },
                }}
              >
                {generando
                  ? "Consultando..."
                  : modo === "oficio"
                  ? "Generar"
                  : "Agregar a la tabla"}
              </Button>
            </Box>
          </Box>
        </Box>

        {generando && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "30vh",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>
              Calculando devengos, esto puede tardar varios minutos...
            </Typography>
          </Box>
        )}

        {!generando && error && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(198, 40, 40, 0.3)",
              background: "rgba(198, 40, 40, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#C62828" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#C62828" }}>
              {error}
            </Typography>
          </Paper>
        )}

        {!generando && mensajeOk && !error && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(46, 125, 50, 0.3)",
              background: "rgba(46, 125, 50, 0.08)",
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: "#2E7D32" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#2E7D32" }}>
              {mensajeOk}
            </Typography>
          </Paper>
        )}

        {!generando && filas.length > 0 && (
          <PageBgBlock pad="0">
            <Paper elevation={0} sx={{ border: "1px solid rgba(0,0,0,0.15)", mb: 3 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid rgba(0,0,0,0.12)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {totales.rbd} de {filas.length} RBD seleccionados
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: "rgba(0,0,0,0.60)" }}
                  >
                    &nbsp;&nbsp;{totales.periodos.toLocaleString("es-CL")} períodos
                    &nbsp;·&nbsp; devengado {formatoMonto(totales.devengado)}
                  </Box>
                </Typography>

                <Box sx={{ flex: 1 }} />

                <Button
                  size="small"
                  onClick={quitarNoSeleccionados}
                  disabled={totales.rbd === filas.length}
                  sx={{ textTransform: "none" }}
                >
                  Quitar no seleccionados
                </Button>
                <Button size="small" onClick={limpiarTabla} sx={{ textTransform: "none" }}>
                  Limpiar tabla
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  disabled={totales.rbd === 0}
                  onClick={descargarExcel}
                  sx={{
                    background: "#C8D8F0",
                    color: "#1565c0",
                    boxShadow: "none",
                    textTransform: "none",
                    "&:hover": { background: "#b3c8e8", boxShadow: "none" },
                  }}
                >
                  Descargar Excel
                </Button>
              </Box>

              <TableContainer sx={{ maxHeight: "60vh" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={filas.length > 0 && totales.rbd === filas.length}
                          indeterminate={totales.rbd > 0 && totales.rbd < filas.length}
                          onChange={alternarTodos}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>RBD</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Zona</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Operador</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Períodos</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Valor a facturar
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Descuento
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Devengado
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filas.map((f) => (
                      <TableRow
                        key={f.rbd}
                        hover
                        selected={seleccionados.has(f.rbd)}
                        onClick={() => alternarRbd(f.rbd)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={seleccionados.has(f.rbd)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => alternarRbd(f.rbd)}
                          />
                        </TableCell>
                        <TableCell>{f.rbd}</TableCell>
                        <TableCell>{f.zona ?? "—"}</TableCell>
                        <TableCell>{nombreOperador(f.id_operador)}</TableCell>
                        <TableCell>
                          {f.periodos_devengables ?? f.periodos}
                          {f.periodos_devengables != null &&
                            f.periodos > f.periodos_devengables && (
                              <Box
                                component="span"
                                sx={{ color: "#1565c0", ml: 0.5 }}
                              >
                                +{f.periodos - f.periodos_devengables} mes sig.
                              </Box>
                            )}
                          <Box
                            component="span"
                            sx={{ color: "rgba(0,0,0,0.60)", ml: 0.5 }}
                          >
                            ({etiquetaPeriodo(f.periodo_inicio)} a{" "}
                            {etiquetaPeriodo(f.periodo_fin)})
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatoMonto(f.total_valor_a_facturar)}
                        </TableCell>
                        <TableCell align="right">
                          {formatoMonto(f.total_descuento)}
                        </TableCell>
                        <TableCell align="right">
                          {formatoMonto(f.total_devengado)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </PageBgBlock>
        )}

        {!generando && resultadoOficio && (
          <PageBgBlock pad="0">
            <Paper elevation={0} sx={{ border: "1px solid rgba(0,0,0,0.15)" }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Oficio {resultadoOficio.id_oficio}
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: "rgba(0,0,0,0.60)" }}
                  >
                    &nbsp;&nbsp;períodos {resultadoOficio.periodo_inicio} a{" "}
                    {resultadoOficio.periodo_fin}
                  </Box>
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Operador</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        RBD devengados
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Monto total
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">
                        Archivo
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(resultadoOficio.resultados ?? []).map((r) => (
                      <TableRow key={r.id_devengos_operador} hover>
                        <TableCell>{nombreOperador(r.id_operador)}</TableCell>
                        <TableCell align="right">
                          {(r.total_rbd_devengo ?? 0).toLocaleString("es-CL")}
                        </TableCell>
                        <TableCell align="right">{formatoMonto(r.monto_total)}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            href={r.url_excel_devengo}
                            target="_blank"
                            rel="noreferrer"
                            disabled={!r.url_excel_devengo}
                            sx={{ textTransform: "none" }}
                          >
                            Descargar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </PageBgBlock>
        )}

      </Box>
    </PageBody>
  );
}
